import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import * as moment from 'moment';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';

import { TurnoversService } from '@app/services/turnovers.service';
import { WorkflowActionsService } from '@app/services/workflow-actions.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import { SpecialCalendarEvent } from '@main-application/calendar/interfaces/calendar.interfaces';
import { clearAnyNotifications } from '@main-application/notifications/store/actions/notifications.actions';
import { selectUserData } from '@main-application/store/selectors/user.selectors';
import { SummaryPeriodListConstants } from '@shared/constants/summary.const';
import { LocalStorageDataEnum } from '@shared/enums/local-storage-data.enum';
import { RestTurnoverDataModelNew, RestTurnoverTaskModel } from '@shared/interfaces/turnover.interface';
import { UserData } from '@shared/interfaces/user-data';
import { getStorageItem, setStorageItem } from '@shared/utils/extensions';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import { BoardService } from './board.service';
import { CellValueHelper } from './cell-value.helper';
import {
  Board,
  BoardColumn,
  BoardEvent,
  BoardFilter,
  ColumnFilter,
  ColumnSorting,
  RestBoardModel,
  Sorting,
  getNextStepType,
} from '../interfaces/board';
import { BoardColumnType, BoardLayoutType, BoardOutputBehaviour, ScheduleCellStatus } from '../interfaces/board.enums';
import { BoardsConfiguration, ColumnSettings, StoredBoardConfiguration } from '../interfaces/boards-configuration';

@Injectable()
@UntilDestroy()
export class BoardEventService extends PuSubscribable {
  private readonly reservedColumnsEvents = [SpecialCalendarEvent.MoveIn, SpecialCalendarEvent.MoveOut];
  private readonly colorsAmount = 11 - this.reservedColumnsEvents.length; //11 events colors, but some of them is reserved

  private _board: Board;
  private board = new ReplaySubject<Board>(1);
  private _sorting = new BehaviorSubject<ColumnSorting>(null);
  private _filter$ = new BehaviorSubject<BoardFilter>({
    timeframe: SummaryPeriodListConstants.TimeFrame.AllTime,
  });
  private _deletedTurnoverIds = new BehaviorSubject<number[]>([]);
  private readonly _statusFilter$ = new BehaviorSubject<ColumnFilter>(null);
  private readonly _unitTypes$ = new BehaviorSubject<string[]>([]);

  board$ = this.board.asObservable();
  sorting$ = this._sorting.asObservable();
  filter$ = this._filter$.asObservable();
  deletedTurnoverIds$ = this._deletedTurnoverIds.asObservable();
  turnoverChanged$ = new Subject<RestTurnoverDataModelNew>();
  columnChanged$ = new Subject<BoardColumn>();
  selectColumn$ = new Subject<{ turnId: number; columnType: BoardColumnType }>();
  columnCreationCompleted$ = new Subject<void>();
  readonly statusFilter$ = this._statusFilter$.asObservable();
  readonly unitTypes$ = this._unitTypes$.asObservable();

  documentsLoading$ = new BehaviorSubject<boolean>(false);
  documentsLoading = this.documentsLoading$.asObservable();

  boardEvents$: Observable<BoardEvent[]> = this.board$.pipe(
    map(board => [
      ...this.reservedColumnsEvents.map((e, i) => ({ name: e, id: '', colorScheme: i })),
      ...board.columns
        .filter(e => [BoardColumnType.Simple, BoardColumnType.Timed, BoardColumnType.Workorder].includes(e.type))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e, i) => ({
          name: e.name,
          id: e.id,
          colorScheme: (i % this.colorsAmount) + this.reservedColumnsEvents.length,
        })),
    ])
  );

  scheduleColumns$: Observable<BoardColumn[]> = this.board$.pipe(
    map(board => [
      ...board.columns
        .filter(e => [BoardColumnType.Simple, BoardColumnType.MoveIn, BoardColumnType.MoveOut].includes(e.type))
        .sort((a, b) => a.name.localeCompare(b.name)),
    ])
  );

  private _allTurnoversList = new ReplaySubject<RestTurnoverTaskModel[]>(1);
  allTurnoversList$ = this._allTurnoversList.asObservable();
  boardReLoader$ = new BehaviorSubject(new Date());

  private pollingSub: Subscription;
  private _isBoardUpdating$ = new BehaviorSubject(false);
  isBoardUpdating$ = this._isBoardUpdating$.pipe(distinctUntilChanged());

  constructor(
    private boardService: BoardService,
    private workflowActionsService: WorkflowActionsService,
    private turnoverService: TurnoversService,
    private cellValueHelper: CellValueHelper,
    private readonly store: Store<{}>,
    private snackbarService: SnackbarService
  ) {
    super();
    this.filter$
      .pipe(
        withLatestFrom(this.store.select(selectUserData)),
        tap(([filter, userData]) => {
          if (this._board?.id) {
            const key = `${LocalStorageDataEnum.BOARD_FILTER}-${this._board.id}-${userData.id}`;
            if (filter) {
              const { scheduleByStatusFilter, ...filteredFilter } = filter;
              localStorage.setItem(key, JSON.stringify(filteredFilter));
            } else {
              localStorage.removeItem(key);
            }
          }
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  setNewTurnoverList(list: RestTurnoverTaskModel[]) {
    const uniqueUnitTypes = [...new Set(list.map(item => item.unitType).filter(Boolean))].sort();
    this._unitTypes$.next(uniqueUnitTypes);
    this._allTurnoversList.next(list);
  }

  addNewSingleTurnToList(turn: RestTurnoverTaskModel) {
    turn.isNew = true;
    this._allTurnoversList
      .pipe(take(1))
      .subscribe(list => this._allTurnoversList.next([turn, ...list]))
      .untilDestroyed(this);
  }

  resetNewFlagForTurns() {
    this._allTurnoversList
      .pipe(take(1))
      .subscribe(list => {
        list.forEach(e => {
          if (typeof e.isNew === 'boolean') {
            delete e.isNew;
          }
        });
        this._allTurnoversList.next(list);
      })
      .untilDestroyed(this);
  }

  setSorting(column: BoardColumn, sorting: Sorting, primaryValue = true) {
    if (sorting) {
      this._sorting.next(new ColumnSorting(column, sorting, primaryValue, this.cellValueHelper));

      if (!this._board) {
        return;
      }

      const portfolioId = new URLSearchParams(window.location.search).get('portfolioId');
      const key = this.getSortingStorageKey(this._board.id.toString(), portfolioId);
      localStorage.setItem(
        key,
        JSON.stringify({
          columnId: column.id,
          sorting: sorting,
          primaryValue: primaryValue,
        })
      );
    } else {
      this._sorting.next(new ColumnSorting(column, null, primaryValue, this.cellValueHelper));

      const portfolioId = new URLSearchParams(window.location.search).get('portfolioId');
      const key = this.getSortingStorageKey(this._board.id.toString(), portfolioId);
      localStorage.removeItem(key);
    }
  }

  setFilter(filter: BoardFilter | null) {
    if (!isEqual(this._filter$.value, filter)) {
      this._filter$.next(filter);
    }
  }

  getFilter() {
    return this._filter$.value;
  }

  moveAndRemoveTurnover(turnover: RestTurnoverTaskModel) {
    const stepType = getNextStepType(this._board.outputAction, turnover);

    return this.workflowActionsService.moveToNextStepWithDefaultAssignee(turnover, stepType, this._board.id).pipe(
      tap(result => {
        if (result && this._board.outputBehaviour === BoardOutputBehaviour.Automatically) {
          this._deletedTurnoverIds.next([...this._deletedTurnoverIds.value, turnover.id]);
        }
      }),
      catchError(err => {
        console.error(err);
        return EMPTY;
      })
    );
  }

  setBoard(board: Board, userData: UserData) {
    this._board = board;
    if (this._board?.id !== board.id) {
      this._sorting.next(null);
      this.store.dispatch(clearAnyNotifications());
    }

    const portfolioId = new URLSearchParams(window.location.search).get('portfolioId');
    const key = this.getSortingStorageKey(board.id.toString(), portfolioId);
    const savedSorting = localStorage.getItem(key);

    if (savedSorting) {
      const { columnId, sorting, primaryValue } = JSON.parse(savedSorting);
      const column = board.columns.find(col => col.id === columnId);
      if (column) {
        this.setSorting(column, sorting, primaryValue);
      }
    } else {
      const column = board.columns[0];
      if (column) {
        this.setSorting(column, null, false);
      }
    }
    this.board.next(board);

    const urlParams = new URLSearchParams(window.location.search);
    const urlFilter: BoardFilter = {};
    let hasUrlFilters = false;

    if (urlParams.has('assigneeId')) {
      const assigneeIds = urlParams
        .get('assigneeId')
        .split(',')
        .map(id => Number(id.trim()))
        .filter(id => !isNaN(id));
      if (assigneeIds?.length > 0) {
        urlFilter.assignees = assigneeIds;
        hasUrlFilters = true;
      }
    }

    if (urlParams.has('stepType')) {
      const stepTypes = urlParams
        .get('stepType')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      if (stepTypes?.length > 0) {
        urlFilter.schedule = stepTypes;
        hasUrlFilters = true;
      }
    }

    if (urlParams.has('unitType')) {
      const unitTypes = urlParams
        .get('unitType')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      if (unitTypes?.length > 0) {
        urlFilter.unitTypes = unitTypes;
        hasUrlFilters = true;
      }
    }

    if (urlParams.has('status')) {
      const statusValues = urlParams
        .get('status')
        .split(',')
        .map(s => s.trim())
        .map(status => {
          const enumValue = ScheduleCellStatus[status as keyof typeof ScheduleCellStatus];
          if (typeof enumValue === 'number') {
            return enumValue;
          }
          return null;
        })
        .filter((value): value is ScheduleCellStatus => value !== null);

      if (statusValues?.length > 0) {
        urlFilter.status = statusValues;
        hasUrlFilters = true;
      }
    }

    const currentFilter = this._filter$.value;
    const newFilter = hasUrlFilters
      ? urlFilter
      : this.getStoredBoardFilter(board, userData) ?? { timeframe: SummaryPeriodListConstants.TimeFrame.AllTime };

    if (!isEqual(currentFilter, newFilter)) {
      this.setFilter(newFilter);
    }
  }

  refreshBoardView() {
    if (this._board) {
      const updatedBoard = this._board.clone({ viewRevision: Date.now() });
      this.board.next(updatedBoard);
    }
  }

  startPolling(boardId: number) {
    this.stopPolling();
    this._isBoardUpdating$.next(true);
    this.snackbarService.info('Board data update is in progress…', null, false);
    this.pollingSub = timer(0, 5000)
      .pipe(switchMap(() => this.boardService.getBoardById(boardId)))
      .subscribe(board => {
        if (board.dataSourceChangesProcessedOn) {
          window.location.reload();
        }
      })
      .untilDestroyed(this);
  }

  stopPolling() {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
      this.pollingSub = null;
    }
    if (this._isBoardUpdating$.value) {
      this._isBoardUpdating$.next(false);
    }
    this.snackbarService.dismiss();
  }

  getBoard() {
    return this._board;
  }

  updateTurnoversList(portfolioId: number, updateAttachments = false) {
    const attachmentColumn = this.getBoard().columns.find(col => col.type === BoardColumnType.Documents);
    const includeAttachments = !!attachmentColumn;
    const isProjectBoard = this.getBoard().boardLayoutType === BoardLayoutType.ProjectsBoard;
    this.boardService
      .getTurnoverList(this._board.id, portfolioId, includeAttachments, isProjectBoard)
      .pipe(take(1))
      .subscribe(list => {
        this.setNewTurnoverList(list);

        if (updateAttachments) {
          this.notifyColumnChanged(attachmentColumn);
          this.documentsLoading$.next(false);
        }
      })
      .untilDestroyed(this);
  }

  save(portfolioId?: number | undefined) {
    this.boardService
      .update(new RestBoardModel(this._board))
      .subscribe({
        next: () => {
          if (portfolioId) {
            this.updateTurnoversList(portfolioId);
          }
        },
        error: err => {
          console.error('Failed to update board:', err);
          const errorMsg = err?.error?.errorCode
            ? err.error.errorCode
            : `Failed to update ${this._board.name ?? 'the board'}. Please try again.`;
          this.snackbarService.error(errorMsg);
        },
      })
      .untilDestroyed(this);

    this.notifyColumnCreationCompleted();
  }

  updateBoardStore(config: Partial<StoredBoardConfiguration>) {
    this.getBoardConfigWithGlobal()
      .subscribe(({ configs, boardConfig }) => {
        Object.assign(boardConfig, config);
        setStorageItem(LocalStorageDataEnum.BOARD_BUILDER, configs);
      })
      .untilDestroyed(this);
  }

  updateColumnStore(columnId: string, portfolioId: number | undefined, config: ColumnSettings | null | undefined) {
    this.getBoardConfigWithGlobal()
      .subscribe(({ configs, boardConfig }) => {
        const storageKey = this.getConfigKey(columnId, portfolioId);
        if (portfolioId && boardConfig.columnSettings[columnId]) {
          delete boardConfig.columnSettings[columnId];
        }

        const columnConfiguration = (boardConfig.columnSettings[storageKey] ??= {} as ColumnSettings);
        if (!config || typeof config !== 'object' || Object.keys(config).length === 0) {
          for (const key in columnConfiguration) {
            if (Object.prototype.hasOwnProperty.call(columnConfiguration, key)) {
              delete columnConfiguration[key];
            }
          }
        } else {
          Object.assign(columnConfiguration, config);
        }
        setStorageItem(LocalStorageDataEnum.BOARD_BUILDER, configs);
      })
      .untilDestroyed(this);
  }

  private getStoredBoardFilter(board: Board, userData: UserData): BoardFilter | null {
    const key = `${LocalStorageDataEnum.BOARD_FILTER}-${board.id}-${userData.id}`;
    const storedFilter = localStorage.getItem(key);

    if (!storedFilter) return null;

    const parsedFilter: BoardFilter = JSON.parse(storedFilter);

    const { scheduleByStatusFilter, ...filteredFilter } = parsedFilter || {};

    return filteredFilter as BoardFilter;
  }

  private getBoardConfigWithGlobal() {
    return combineLatest([this.store.select(selectUserData), this.board$]).pipe(
      untilDestroyed(this),
      filter(([user, board]) => user && !!board?.id),
      take(1),
      map(([user, board]) => {
        const configs =
          getStorageItem<{ [userId: number]: BoardsConfiguration }>(LocalStorageDataEnum.BOARD_BUILDER) ?? {};
        const userConfig = (configs[user.id] ??= { boardSettings: {} });
        const boardConfig = (userConfig.boardSettings[board.id] ??= { columnSettings: {} });
        return { configs, boardConfig };
      })
    );
  }

  getBoardConfig() {
    return this.getBoardConfigWithGlobal().pipe(map(({ boardConfig }) => boardConfig));
  }

  saveTurnover(
    turnoverId: number,
    boardId: number,
    field: keyof RestTurnoverDataModelNew,
    path: string,
    value: any,
    shouldBeRemoved = false
  ) {
    switch (field) {
      case 'additionalData': {
        return this.turnoverService.patchAdditionalData(turnoverId, boardId, [
          {
            op: shouldBeRemoved ? 'remove' : path.includes('/-') ? 'add' : 'replace',
            path,
            value,
          },
        ]);
      }
      default: {
        return this.turnoverService.patch(turnoverId, [
          {
            op: 'replace',
            path,
            value,
          },
        ]);
      }
    }
  }

  notifyTurnoverChanged(turnoverData: RestTurnoverDataModelNew) {
    this.turnoverChanged$.next(turnoverData);
  }

  clearRemovedTurnovers() {
    this._deletedTurnoverIds.next([]);
  }

  notifyColumnChanged(column: BoardColumn) {
    this.columnChanged$.next(column);
  }

  notifyColumnCreationCompleted() {
    this.columnCreationCompleted$.next();
  }

  get columnCreationCompleted(): Observable<void> {
    return this.columnCreationCompleted$.asObservable();
  }

  getSortingStorageKey(boardId: string, portfolioId: string): string {
    return `${LocalStorageDataEnum.BOARD_BUILDER_SORTING_KEY}${boardId}-${portfolioId}`;
  }

  clearStatusFilter(): void {
    this._statusFilter$.next(null);
  }

  setStatusFilter(columnId: string, status: ScheduleCellStatus): void {
    this._statusFilter$.next(new ColumnFilter(columnId, status));
  }

  calculateNewStatus(schedule: any): ScheduleCellStatus {
    const currentDate = moment().startOf('day').toDate();
    if (schedule.dueDate && schedule.dueDate < currentDate) {
      if (schedule.status === ScheduleCellStatus.Due || schedule.status === ScheduleCellStatus.Moved) {
        return ScheduleCellStatus.Late;
      }
    } else if (schedule.status === ScheduleCellStatus.Late && schedule.dueDate >= currentDate) {
      return ScheduleCellStatus.Due;
    }

    if (
      schedule.status === ScheduleCellStatus.Due &&
      schedule.dueDate &&
      schedule.originalDueDate &&
      schedule.dueDate.getTime() !== schedule.originalDueDate.getTime()
    ) {
      return ScheduleCellStatus.Moved;
    }

    return schedule.status;
  }

  getConfigKey(columnId: string, portfolioId?: number): string {
    return portfolioId ? `${columnId}_${portfolioId}` : columnId;
  }
}
